// Полоса разделов — левый край скелета (ПРАВИЛА.md, 1.1). Показывает только разделы,
// доступные по правам: скрытие здесь — удобство навигации, изоляция данных живёт на
// сервере выборкой. Раздел без настоящих экранов (notReady) остаётся кликабельным и
// ведёт на честную заглушку с причиной — а не исчезает и не сереет молча.

import { useState } from 'react';

import { RiLogoutBoxRLine } from '@remixicon/react';
import { useRef } from 'react';

import { Link, useLocation, useNavigate } from 'react-router';

import { ЗНАЧКИ_РАЗДЕЛОВ } from '@/app/значки-разделов';
import { ЗакрытьСвоюЗаписьДиалог } from '@/layout/ЗакрытьСвоюЗаписьДиалог';
import { МойВходДиалог } from '@/layout/МойВходДиалог';
import { ЗначокОбновления, useОткрытьИсторию } from '@/layout/ЧтоНового';
import { useСреда } from '@/shared/среда';
import { logout } from '@/shared/auth/session';
import { useAuthStore } from '@/shared/auth/store';
import { Палитра } from '@/shared/palette/Палитра';
import { NotificationsBell } from '@/shared/notifications/NotificationsBell';
import { Подсказка } from '@/shared/hints/Подсказка';
import { ThemeSwitch } from '@/shared/theme/ThemeSwitch';
import { defaultViewFor, visibleSections } from '@/shared/rbac/permissions';
import barkMark from '@/shared/ui/brand/bark-mark.png';
import { cn } from '@/utils/cn';

export function SectionRail() {
  const user = useAuthStore((state) => state.user);
  const permissions = useAuthStore((state) => state.permissions);
  // Подсказки указывают на элемент ссылкой: обёртка вокруг него ломала бы раскладку.
  const якорьПомощника = useRef<HTMLDivElement>(null);
  const якорьТемы = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [leaving, setLeaving] = useState(false);
  const [мойВходОткрыт, setМойВходОткрыт] = useState(false);
  const [закрытиеОткрыто, setЗакрытиеОткрыто] = useState(false);
  const открытьИсторию = useОткрытьИсторию();

  const sections = visibleSections(permissions);

  const среда = useСреда();
  const узкаяПолоса = среда.ширина !== 'широко';

  async function handleLogout() {
    if (leaving) {
      // Гейт с объяснением вместо тихого повтора: выход уже идёт, второй не нужен.
      return;
    }
    setLeaving(true);
    try {
      await logout();
    } finally {
      setLeaving(false);
      navigate('/login', { replace: true });
    }
  }

  return (
    // На бумагу полоса разделов не идёт: лист узкий, и навигация съедает его четверть,
    // ничего не сообщая тому, кто держит документ в руках.
    <aside
      data-печать='скрыть'
      className='flex h-full w-rail shrink-0 flex-col border-r border-stroke-soft-200 bg-bg-white-0 тесно:w-rail-tight'
    >
      {/* На узкой полосе остаётся один знак: слова «Барк, охранный персонал» человек,
          который уже вошёл в программу, читает ровно ноль раз, а места они занимают как
          полтора раздела. */}
      <div className='flex items-center gap-2.5 px-4 pb-4 pt-5 тесно:justify-center тесно:px-2'>
        <img src={barkMark} alt='' className='h-8 w-auto shrink-0' />
        <div className='flex flex-col тесно:hidden'>
          <span className='text-label-sm text-text-strong-950'>Барк</span>
          <span className='text-paragraph-xs text-text-sub-600'>охранный персонал</span>
        </div>
      </div>

      <nav aria-label='Разделы' className='flex flex-1 flex-col gap-1 overflow-y-auto px-3 py-2'>
        {sections.map((section) => {
          const Icon = ЗНАЧКИ_РАЗДЕЛОВ[section.slug];
          // Раздел активен на любом своём представлении и строке, а не только на
          // представлении по умолчанию, куда ведёт сама ссылка.
          const isActive =
            location.pathname === `/${section.slug}` ||
            location.pathname.startsWith(`/${section.slug}/`);
          return (
            <Link
              key={section.slug}
              to={`/${section.slug}/${defaultViewFor(section, permissions)}`}
              aria-current={isActive ? 'page' : undefined}
              className={cn(
                'group flex items-center gap-2.5 rounded-10 px-3 py-2 text-label-sm transition duration-200 ease-out',
                // Подпись под значком, а не голый значок: смысл, спрятанный за картинкой, —
                // это место, куда надо догадаться зайти. Всплывающих подсказок на планшете
                // не бывает вовсе, то есть догадываться пришлось бы наугад.
                'тесно:min-h-14 тесно:flex-col тесно:gap-1 тесно:px-1 тесно:py-1.5 тесно:text-center тесно:text-paragraph-xs тесно:leading-tight',
                isActive
                  ? 'bg-bg-weak-50 text-text-strong-950'
                  : 'text-text-sub-600 hover:bg-bg-weak-50 hover:text-text-strong-950',
              )}
            >
              <Icon
                className={cn(
                  'size-5 shrink-0 transition duration-200 ease-out',
                  isActive
                    ? 'text-primary-base'
                    : 'text-text-sub-600 group-hover:text-text-sub-600',
                )}
              />
              {section.title}
            </Link>
          );
        })}
      </nav>

      {/* Помощник стоит НАД строкой человека и под разделами: это инструмент, а не раздел, и
          места среди разделов ему нет (потолок разделов — правило 12). Виден с любого экрана. */}
      {/* Поле 12 и зазор 10 — те же, что у разделов выше: 12 (полоса) + 12 (поле строки) +
          20 (значок) + 10 = текст на 54. Ровно на этой вертикали стоят названия разделов.
          До 25.08.2026 здесь было три разные геометрии, и текст начинался на 48, 54 и 56 —
          то самое «поля прыгают», которое пальцем не показать. Поймала новая сквозная
          проверка, а не глаз. */}
      <div ref={якорьПомощника} className='border-t border-stroke-soft-200 px-3 py-2'>
        <Палитра />
      </div>
      <Подсказка урок='помощник' якорь={якорьПомощника} />

      {/* «Есть обновление» — над строкой человека, в нижней группе: туда смотрят за своим, и там
          же он виден с любого экрана. Появляется, только когда новая версия ждёт установки, и
          не уходит по «Позже» вместе с полосой внизу окна. */}
      <ЗначокОбновления />

      {/* Низ полосы упирается в полосу жеста «домой»: без отступа безопасной зоны кнопка
          выхода оказывается ровно под ней, и нажатие уводит из приложения вместо выхода. */}
      <div className='flex items-center gap-2.5 border-t border-stroke-soft-200 px-3 py-3 тач:безопасно-снизу тач:безопасно-слева тесно:flex-col тесно:gap-3 тесно:px-1'>
        <NotificationsBell />
        <div className='flex min-w-0 flex-1 flex-col тесно:w-full тесно:flex-none'>
          {/* Имя — кнопка «мой вход». Единственное место, откуда человек может сменить себе
              фразу: раздел «Настройки» открыт не всем ролям, а имя видно с любого экрана.
              До 09.09.2026 сменить себе фразу не мог никто, включая финансового директора. */}
          {user === null ? (
            <span className='truncate text-label-sm text-text-strong-950'>—</span>
          ) : (
            <button
              type='button'
              onClick={() => setМойВходОткрыт(true)}
              title='Мой вход: имя и фраза'
              className='цель-тача relative truncate text-left text-label-sm text-text-strong-950 transition duration-200 ease-out hover:text-primary-base тесно:w-full тесно:text-center тесно:text-paragraph-xs'
            >
              {/* «Багровский С. В.» в 72 точки не помещается ни при каком размере шрифта, а
                  обрезанная до «Багр…» фамилия не сообщает ничего. Слово «Вход» отвечает на
                  тот же вопрос: за этой кнопкой моё имя и моя фраза.
                  Выбор кодом, а не прятанием: спрятанный вариант остаётся в разметке и
                  уезжает в чтение с экрана вторым именем той же кнопки. */}
              {узкаяПолоса ? 'Вход' : user.display_name}
            </button>
          )}
          {/* «Вход выполнен» стояло здесь всегда и не сообщало ничего: имя человека прямо
              над ним, а экран, который он видит, и есть доказательство входа. Вторая строка
              нужна ровно в один момент — пока идёт выход, — и до 02.09.2026 она вместо этого
              съедала ширину: с появлением переключателя оформления строка «Вход выполнен»
              переносилась надвое и раздвигала весь низ полосы. */}
          {leaving && <span className='text-paragraph-xs text-text-sub-600'>Выходим…</span>}
        </div>
        <div ref={якорьТемы} className='shrink-0'>
          <ThemeSwitch />
        </div>
        <Подсказка урок='тема-оформления' якорь={якорьТемы} />
        <button
          type='button'
          onClick={() => void handleLogout()}
          aria-label='Выйти'
          title='Выйти'
          className='цель-тача relative flex size-8 shrink-0 items-center justify-center rounded-lg text-text-soft-400 transition duration-200 ease-out hover:bg-bg-weak-50 hover:text-text-strong-950'
        >
          <RiLogoutBoxRLine className='size-5' />
        </button>
      </div>

      {user !== null && (
        <>
          <МойВходДиалог
            открыт={мойВходОткрыт}
            закрыть={() => setМойВходОткрыт(false)}
            имяВхода={user.login}
            открытьИсторию={() => {
              // Окно закрывается, панель открывается: список изменений читают, не держа
              // перед глазами форму смены фразы.
              setМойВходОткрыт(false);
              открытьИсторию();
            }}
            открытьЗакрытие={() => {
              // Одно окно закрывается, другое открывается: два вопроса «вы точно?» друг над
              // другом человек читает как один и жмёт вслепую.
              setМойВходОткрыт(false);
              setЗакрытиеОткрыто(true);
            }}
          />
          <ЗакрытьСвоюЗаписьДиалог
            открыт={закрытиеОткрыто}
            закрыть={() => setЗакрытиеОткрыто(false)}
            имяВхода={user.login}
            послеЗакрытия={async () => {
              setЗакрытиеОткрыто(false);
              // Тем же путём, что обычный выход: ключи уже мертвы на сервере, здесь снимаем
              // их с этой машины и уводим на вход, а не оставляем пустой экран.
              await logout();
              navigate('/login', { replace: true });
            }}
          />
        </>
      )}
    </aside>
  );
}
